'use client'

import React, { useMemo } from 'react'
import { Box, Button, Flex, HStack, Text, useBreakpointValue } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { useSelector } from 'react-redux'
import { shallowEqual } from 'react-redux'
import Link from 'next/link'
import NumberToPrice from '@/app/[locale]/_components/NumberToPrice'
import { IRedux } from '@/app/[locale]/_store/types'
import { createUrlWithParams } from '@/app/[locale]/_utils/createUrlWithParams'
import { useDeliveryPrice } from '@/app/[locale]/_hooks/useDeliveryPrice'

export default function MobileCartBar() {
  const t = useTranslations()
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false })

  const { cart, order_type, settings } = useSelector(
    (state: IRedux) => ({
      cart: state.cart.cart,
      order_type: state.session.order_type,
      settings: state.settings.data,
    }),
    shallowEqual
  )

  const orderTotal = useMemo(() => {
    if (!cart?.length) return 0
    return cart.reduce((acc, product) => {
      let productTotal = (product.price_with_discount ?? product.price ?? 0) * product.quantity
      if (product.order_modifiers?.length) {
        product.order_modifiers.forEach((mod: { modifier_price?: number; modifier_quantity?: number }) => {
          productTotal +=
            +(mod.modifier_price ?? 0) * +(mod.modifier_quantity ?? 0) * product.quantity
        })
      }
      return acc + productTotal
    }, 0)
  }, [cart])

  const isDelivery = order_type?.type === 'delivery'
  const showDeliveryCost = isDelivery && settings?.data?.is_home_page_delivery 
  const { deliveryPrice } = useDeliveryPrice(orderTotal, { isHomePage: true, enabled: showDeliveryCost })
  const totalWithDelivery = orderTotal + (showDeliveryCost ? deliveryPrice : 0)

  if(!showDeliveryCost) return null
  if (!isMobile || !cart?.length || orderTotal <= 0) return null

  return (
    <Box
      position='fixed'
      bottom={'70px'}
      left={0}
      right={0}
      zIndex={20}
      bg='background.card'
      borderTop='1px solid'
      borderColor='border'
      px={4}
      pt={3}
      pb={5}
    >
      <Flex flexDirection='column' gap={2} mb={3}>
        <HStack justifyContent='space-between' fontSize='sm' fontWeight={500}>
          <Text color='text'>{t('order_sum')}</Text>
          <NumberToPrice
            value={orderTotal}
            textProps={{ fontWeight: 600, fontSize: 'sm', color: 'text' }}
            spanProps={{ fontWeight: 600, fontSize: 'sm', color: 'text' }}
          />
        </HStack>
        {showDeliveryCost && (
          <HStack justifyContent='space-between' fontSize='sm' fontWeight={500}>
            <Text color='text'>{t('delivery_cost')}</Text>
            {deliveryPrice === 0 ? (
              <Text fontWeight={600} fontSize='sm' color='text'>{t('free')}</Text>
            ) : (
                <NumberToPrice
                  value={deliveryPrice}
                  textProps={{ fontWeight: 600, fontSize: 'sm', color: 'text' }}
                  spanProps={{ fontWeight: 600, fontSize: 'sm', color: 'text' }}
                />
            )}
          </HStack>
        )}
      </Flex>
      <Button
        asChild
        w='full'
        h='52px'
        rounded={16}
        fontWeight={600}
        fontSize='sm'
        bg='primary'
        color='primary.text'
        _hover={{ bg: 'primary.hover' }}
      >
        <Link href={createUrlWithParams('/cart')}>
          <HStack w='full' justify='space-between' px={2}>
            <Text>{t('cart')}</Text>
            <NumberToPrice
              value={totalWithDelivery}
              textProps={{ fontWeight: 600, fontSize: 'sm', color: 'primary.text' }}
              spanProps={{ fontWeight: 600, fontSize: 'sm', color: 'primary.text' }}
            />
          </HStack>
        </Link>
      </Button>
    </Box>
  )
}
